import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import type { AuditLog, PaginatedResponse } from '@/types/api';

export function useAuditLogs(params: {
  org_id?: string | null;
  actor_id?: string | null;
  actor_q?: string | null;
  action?: string | null;
  limit?: number;
  offset?: number;
}) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: [
      'audit',
      params.org_id ?? null,
      params.actor_id ?? null,
      params.actor_q ?? null,
      params.action ?? null,
      limit,
      offset,
    ],
    queryFn: async () =>
      (
        await api.get<PaginatedResponse<AuditLog>>('/audit', {
          params: {
            organization_id: params.org_id ?? undefined,
            actor_id: params.actor_id ?? undefined,
            actor_q: params.actor_q ?? undefined,
            action: params.action ?? undefined,
            limit,
            offset,
          },
        })
      ).data,
  });
}

export interface AuditExportFilters {
  org_id?: string | null;
  actor_id?: string | null;
  action?: string | null;
  limit?: number | null;
}

export async function exportAuditCsv(filters: AuditExportFilters): Promise<void> {
  const response = await api.get<Blob>('/audit/export.csv', {
    responseType: 'blob',
    params: {
      organization_id: filters.org_id ?? undefined,
      actor_id: filters.actor_id ?? undefined,
      action: filters.action ?? undefined,
      limit: filters.limit ?? undefined,
    },
  });
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-${format(new Date(), 'yyyyMMdd')}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

interface ActivityResponse {
  items: AuditLog[];
  total: number;
}

export function useFileActivity(fileId: string | undefined) {
  return useQuery({
    enabled: !!fileId,
    queryKey: ['audit', 'file', fileId],
    queryFn: async () =>
      (await api.get<ActivityResponse>(`/files/${fileId}/activity`)).data,
  });
}

export function useRequestActivity(requestId: string | undefined) {
  return useQuery({
    enabled: !!requestId,
    queryKey: ['audit', 'request', requestId],
    queryFn: async () =>
      (await api.get<ActivityResponse>(`/requests/${requestId}/activity`)).data,
  });
}
